import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import PublicNav from "@/components/nav/PublicNav";
import Footer from "@/components/nav/Footer";
import { ArrowRight, Check, X } from "lucide-react";
import { TrialCTA } from "@/components/trial/TrialCTA";

export default function Home() {
  const [employees, setEmployees] = useState([]);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (loc.hash) {
      const id = loc.hash.replace("#", "");
      // wait a tick so the section is in the DOM
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    }
  }, [loc.hash]);

  return (
    <div className="min-h-screen bg-obsidian text-ivory grain">
      <PublicNav />

      {/* HERO */}
      <section className="relative border-b border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-24 md:pt-28 md:pb-32">
          <div className="brand-eyebrow mb-8 animate-fade-up">A NEW KIND OF EMPLOYEE</div>
          <h1 data-testid="hero-headline" className="brand-headline text-[56px] sm:text-[88px] md:text-[128px] lg:text-[152px] tracking-tightest animate-fade-up">
            <span className="block text-ivory italic">Your next employee</span>
            <span data-testid="hero-headline-accent" className="block text-purple">isn&apos;t human.</span>
          </h1>
          <p data-testid="hero-value-line" className="text-gold/80 max-w-2xl text-base md:text-lg mt-8 leading-relaxed animate-fade-up tracking-wide">
            No platform fees. No software bundle. Pay only for the employee you hire.
          </p>
          <p data-testid="hero-sub" className="text-steel max-w-2xl text-lg md:text-xl mt-6 leading-relaxed animate-fade-up">
            Build <span className="text-ivory">AI employees</span> that handle the work, so you can focus on what matters.
          </p>

          <div className="mt-12">
            <div className="flex flex-wrap items-start gap-6">
              <TrialCTA testid="hero-cta" />
              <a data-testid="hero-cta-secondary" href="#how" className="btn-outline">
                See How It Works
              </a>
            </div>
          </div>

          {/* Flow visual */}
          <div className="mt-20 border border-line p-8 md:p-10 bg-graphite/40">
            <div className="brand-eyebrow mb-8">HOW A TASK MOVES</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
              <FlowCard label="YOU" body='"Handle these 47 requests."' step="01" />
              <div className="hidden md:flex items-center"><div className="flow-line w-full" /></div>
              <FlowCard label="HIRED" body="Reads. Organizes. Drafts. Updates. Flags." step="02" highlight />
            </div>
            <div className="hidden md:block mt-6 flow-line w-full" />
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div />
              <div />
              <FlowCard label="DONE" body="Completed work waiting for review." step="03" />
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="border-b border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="brand-eyebrow mb-6">THE PROBLEM</div>
          <h2 className="brand-headline text-5xl md:text-7xl max-w-3xl">Stop hiring software.<br /><span className="text-purple italic">Start hiring help.</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-16">
            <div className="border border-line p-10 bg-graphite/40">
              <div className="brand-eyebrow mb-6">TRADITIONAL SOFTWARE</div>
              {["Learn a dashboard nobody uses","Copy data between five tools","Chase people to update fields","Reports that go stale in a week","Automation you configure forever"].map((t) => (
                <div key={t} className="flex items-start gap-3 py-3 border-t border-line first:border-t-0">
                  <X size={18} className="text-steel shrink-0 mt-0.5" />
                  <div className="text-steel">{t}</div>
                </div>
              ))}
            </div>
            <div className="card-white p-10">
              <div className="brand-eyebrow mb-6" style={{color:'#0B0D10'}}>HIRED — AN EMPLOYEE</div>
              {["Tell them the outcome","They do the work","They chase people, not you","They summarize weekly","They ask when they're unsure"].map((t) => (
                <div key={t} className="flex items-start gap-3 py-3 border-t first:border-t-0" style={{borderColor:'#e5e0d3'}}>
                  <Check size={18} className="shrink-0 mt-0.5" style={{color:'#7C3AED'}} />
                  <div className="text-obsidian font-medium">{t}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-b border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="brand-eyebrow mb-6">HOW IT WORKS</div>
          <h2 className="brand-headline text-5xl md:text-7xl max-w-3xl mb-16">Four steps.<br /><span className="italic">No implementation team.</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { n: "01", t: "Choose Employee", d: "Pick the AI employee that fits your business." },
              { n: "02", t: "Connect Tools", d: "Link email, calendar, docs, CRM. One click each." },
              { n: "03", t: "Set Rules", d: "Choose what your employee does alone vs. what needs approval." },
              { n: "04", t: "Put Hired to Work", d: "They start. You review. Repeat." },
            ].map((s, i) => (
              <div key={s.n} className="border border-line p-8 hover:border-gold transition-colors bg-graphite/30">
                <div className={`brand-headline text-4xl ${i === 1 ? "text-purple" : "text-gold"}`}>{s.n}</div>
                <div className="mt-6 text-ivory brand-headline text-2xl">{s.t}</div>
                <div className="mt-3 text-steel text-sm">{s.d}</div>
              </div>
            ))}
          </div>

          {/* Task lifecycle */}
          <div className="mt-20" data-testid="how-task-lifecycle">
            <div className="brand-eyebrow mb-4">INSIDE A TASK</div>
            <h3 className="brand-headline text-3xl md:text-5xl max-w-3xl mb-10">
              Every task moves through <span className="text-purple italic">five stages.</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              {[
                { n: "01", t: "Observe",        d: "Watches the inbox, calendar, and connected tools for new signals." },
                { n: "02", t: "Prepare",        d: "Drafts the response, ticket, or update — with context attached." },
                { n: "03", t: "Ask / Approve",  d: "Sensitive actions pause here and wait for your yes." },
                { n: "04", t: "Execute",        d: "Sends, files, books, or updates the moment it's cleared." },
                { n: "05", t: "Log",            d: "Records what happened, who approved it, and the outcome." },
              ].map((s, i) => (
                <div key={s.n} data-testid={`how-stage-${i}`} className="border border-line p-6 bg-graphite/30 hover:border-gold transition-colors">
                  <div className={`text-xs font-mono tracking-widest ${i === 2 ? "text-purple" : "text-gold"}`}>{s.n}</div>
                  <div className="mt-3 text-ivory font-semibold text-lg">{s.t}</div>
                  <div className="mt-2 text-steel text-xs leading-relaxed">{s.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MEET THE TEAM */}
      <section className="border-b border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="brand-eyebrow mb-6">MEET THE TEAM</div>
          <h2 className="brand-headline text-5xl md:text-7xl max-w-3xl mb-16">Employees on the payroll.<br /><span className="text-purple italic">Not another SaaS tool.</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {employees.map((e) => (
              <div key={e.slug} data-testid={`employee-card-${e.slug}`} className={`p-8 ${e.status === "Coming Soon" ? "border border-line opacity-60 bg-graphite/30" : "card-white"}`}>
                <div className="flex items-center justify-between">
                  <div className="brand-eyebrow" style={{color: e.status==="Coming Soon" ? "#D4AF37" : "#0B0D10"}}>{e.industry}</div>
                  <div className={`text-[11px] tracking-widest font-semibold ${e.status==="Coming Soon" ? "text-steel" : "text-purple"}`}>{e.status==="Coming Soon" ? "COMING SOON" : "PREVIEW"}</div>
                </div>
                <div className="brand-headline text-4xl md:text-5xl mt-6">{e.name}</div>
                <div className={`text-sm mt-2 ${e.status==="Coming Soon" ? "text-steel" : "text-obsidian/70"}`}>{e.role_title}</div>
                <div className={`mt-6 text-sm leading-relaxed ${e.status==="Coming Soon" ? "text-steel" : "text-obsidian"}`}>{e.tagline}</div>
                {e.status !== "Coming Soon" && (
                  <div className="flex flex-wrap gap-3 mt-8">
                    <Link data-testid={`meet-${e.slug}`} to={`/hired/${e.slug}`} className="bg-obsidian text-ivory px-5 py-3 text-sm font-semibold hover:bg-purple transition-colors">Meet {e.name}</Link>
                    <Link to={`/hired/${e.slug}#tasks`} className="border border-obsidian text-obsidian px-5 py-3 text-sm font-semibold hover:bg-obsidian hover:text-ivory transition-colors">See What {e.name} Can Do</Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
          <h2 className="brand-headline text-5xl md:text-8xl">Ready to<br /><span className="text-purple italic">hire?</span></h2>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <TrialCTA testid="cta-signup" />
            <Link data-testid="cta-pricing" to="/pricing" className="btn-outline">See Pricing</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FlowCard({ label, body, step, highlight }) {
  return (
    <div className={`p-6 border ${highlight ? "border-gold" : "border-line"} bg-obsidian/60`}>
      <div className="flex items-center justify-between">
        <div className="brand-eyebrow">{label}</div>
        <div className="text-steel text-xs font-mono">{step}</div>
      </div>
      <div className={`mt-4 ${highlight ? "text-gold" : "text-ivory"} text-lg md:text-xl leading-snug`}>{body}</div>
    </div>
  );
}
